import React, { FC, memo, useState } from 'react';
import { useButton, useI18n, useLogger, useSelector } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import { BlackButton, BlueButton, Box, GreenButton } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import usePrintReceipt from '~Hooks/usePrintReceipt';
import { useStorage } from '~Components/Storage';
import InternetStatus from '~Components/InternetStatus';
import { Printer, PrintInQueue } from '~reducers';
import { StyledLink } from '../styles';
import PrinterRow from '../PrinterRow';
import BongGroupHistory from '../BongGroupHistory';
import FailedPrints from '../FailedPrints';
import { STATUS_COLORS, STATUS_ICONS, StatusType } from '../constants';

interface PanelProps {
  status: StatusType;
  printers: Printer[];
  printErrors: PrintInQueue[];
  sendTestBong?: () => void;
}

/** Separate from PrinterStatus so its hooks only run while the panel is open */
const PrinterStatusPanel: FC<PanelProps> = ({ status, printers, printErrors, sendTestBong }) => {
  const { i18n } = useI18n();
  const logger = useLogger('printer-status');

  const venueId = useSelector((state) => state.venueId);
  const isScanning = useSelector((state) => state.POSPrinterScanActive);
  const print = useSelector((state) => state.POSReceiptPrints);
  const POSOrder = useSelector((state) => print && state.POSOrders.find((o) => o.id === print.id));

  const order = POSOrder || print?.order;

  const [storageData] = useStorage();
  const { printReceipt } = usePrintReceipt({ venueId: venueId ?? '' });

  const [justSentBongs, setJustSentBongs] = useState<boolean>(false);

  const headingCopy = {
    OK: i18n('PrinterStatus.Operational'),
    NOT_CONNECTED: i18n('PrinterStatus.NotConnected'),
    WARNING: i18n('PrinterStatus.Warning'),
    ERROR: i18n('PrinterStatus.Error'),
  }[status];

  const descriptionCopy =
    status === 'WARNING'
      ? i18n('PrinterStatus.Warning.Desc')
      : status === 'ERROR'
      ? i18n('PrinterStatus.Error.Desc')
      : null;

  const reconnectButton = useButton({
    onClick: () => {
      logger.info('Running printer discover through printer status view');
      window.postMessageReact('available-printers');
    },
  });

  const manualPrintButton = useButton({
    onClick: () => {
      logger.info('Printing receipt via manual print button');
      printReceipt();
    },
  });

  const sendBongButton = useButton({
    onClick: () => {
      if (justSentBongs || !sendTestBong) {
        return;
      }

      setJustSentBongs(true);

      setTimeout(() => {
        setJustSentBongs(false);
      }, 1500);

      logger.info('Sending manual bong through printer status');
      sendTestBong();
    },
  });

  return (
    <>
      {/** No reason to show internet status if there is no printer added yet */}
      {status !== 'NOT_CONNECTED' && <InternetStatus />}

      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
        gap="small">
        <Icon
          src={STATUS_ICONS[status]}
          color={status === 'NOT_CONNECTED' ? 'var(--colorBlack)' : STATUS_COLORS[status]}
          height="80px"
          width="80px"
        />
        <Box display="flex" flexDirection="column" gap="xs" textAlign="center">
          <Heading level={1}>{headingCopy}</Heading>
          {descriptionCopy && <Paragraph level={3}>{descriptionCopy}</Paragraph>}
        </Box>

        {printers.length > 0 && (
          <Box fluid display="flex" flexDirection="column" textAlign="left" gap="xs">
            <Paragraph level={4}>{i18n('Printer.Status.AddedPrinters')}:</Paragraph>

            <Box fluid display="flex" flexDirection="column" gap="xs">
              {/* List all printers and their status */}
              {printers.map((printer) => (
                <PrinterRow printer={printer} key={printer.id} />
              ))}
            </Box>
          </Box>
        )}

        <StyledLink to="settings-v2/printers">
          <BlueButton fluid>{i18n('PrinterStatus.PrinterSettings')}</BlueButton>
        </StyledLink>

        {/* We hide it if no printers since we have a button further down */}
        {printers.length > 0 && (
          <>
            {sendTestBong && (
              <BlackButton {...sendBongButton} disabled={justSentBongs} fluid>
                {justSentBongs ? i18n('PrinterStatus.Sent') : i18n('PrinterStatus.SendBong')}
              </BlackButton>
            )}

            {/* Run printer discover as a troubleshoot step */}
            {printErrors.length > 0 && (
              <BlackButton {...reconnectButton} disabled={isScanning} fluid>
                {isScanning ? i18n('PrinterStatus.Scanning') : i18n('PrinterStatus.Reconnect')}
              </BlackButton>
            )}
          </>
        )}

        {/* If no printers are connected no need to show these buttons */}
        {printers.length > 0 && (
          <>
            {order && storageData.manualReceiptPrint && (
              <GreenButton {...manualPrintButton} fluid>
                {i18n('PrinterStatus.PrintReceipt')}
              </GreenButton>
            )}
          </>
        )}

        {printErrors.length > 0 && (
          <Box marginTop="small">
            <Heading level={5}>{i18n('Printer.Status.FailedPrints')}:</Heading>
            <FailedPrints />
          </Box>
        )}
      </Box>

      {printErrors.length === 0 && printers.length !== 0 && <BongGroupHistory />}
    </>
  );
};

export default memo(PrinterStatusPanel);
